import React from 'react';
import PropTypes from 'prop-types';
import { camelCase } from 'lodash';
import ReactTable from 'react-table';
import { reduce, ifElse, is, prop, negate } from 'ramda';

import Modal from '../../../common/Modal';
import Money from '../../../common/money';

const breakdownItems = [
  {
    name: 'Net Sales',
    prefix: 'netSales',
    key: 'net',
  },
  {
    name: 'Tips',
    prefix: 'tips',
    key: 'tips',
  },
  {
    name: 'Taxes',
    prefix: ['taxes', 'tax'],
    key: 'tax',
  },
  {
    name: 'Fees',
    prefix: 'fees',
    key: 'fees',
    mask: value => negate(value) || value,
  },
  {
    name: 'Total Collected',
    prefix: 'total',
    key: 'total',
  },
];

const paymentTypes = ['Pos', 'Squire', 'Cash', 'GiftCard'];

const getValueFromData = (prefix, postfix, data) => ifElse(
  is(Array),
  () => reduce(
    (memo, pref) => prop(`${pref}${postfix}`, data) || memo,
    0,
    prefix
  ),
  () => prop(`${prefix}${postfix}`, data),
)(prefix);

// eslint-disable-next-line no-shadow
const buildPaymentsForRow = (breakdownItem, paymentTypes, data) => (
  reduce(
    (memo, paymentType) => {
      const value = getValueFromData(breakdownItem.prefix, paymentType, data);
      return {
        ...memo,
        [camelCase(paymentType)]: breakdownItem.mask ? breakdownItem.mask(value) : value,
      };
    },
    {},
    paymentTypes
  )
);

// eslint-disable-next-line no-shadow
const buildRows = (breakdownItems, paymentTypes, data = {}) => (
  breakdownItems.map(item => ({
    type: item.name,
    ...buildPaymentsForRow(item, paymentTypes, data),
  }))
);

const MAX_ENTRIES = 10000;

const columns = [
  {
    Header: 'Type',
    accessor: 'type',
  },
  {
    Header: 'POS - Card',
    accessor: 'pos',
    className: 'u-text-center',
    Cell: ({ value }) => <Money>{value}</Money>, // eslint-disable-line react/prop-types
  },
  {
    Header: 'Squire - Card',
    accessor: 'squire',
    className: 'u-text-center',
    Cell: ({ value }) => <Money>{value}</Money>, // eslint-disable-line react/prop-types
  },
  {
    Header: 'Cash',
    accessor: 'cash',
    className: 'u-text-center',
    Cell: ({ value }) => <Money>{value}</Money>, // eslint-disable-line react/prop-types
  },
  {
    Header: 'Gift card',
    accessor: 'giftCard',
    className: 'u-text-center',
    Cell: ({ value }) => <Money>{value}</Money>, // eslint-disable-line react/prop-types
  },
];

const CollectedByPaymentModal = ({ items, close, includeColumns, includeRows }) => {
  const filteredBreakdownItems = breakdownItems.filter(item => includeRows.includes(item.key));
  const rows = buildRows(filteredBreakdownItems, paymentTypes, items);
  return (
    <Modal close={close} showCloseButton title="Collected by payment">
      <ReactTable
        data={rows}
        showPagination={false}
        defaultPageSize={MAX_ENTRIES}
        minRows={0}
        resizable={false}
        className="ReactTable ReactTable--row-spacing-only c-inventory-table u-mt+"
        columns={columns.filter(col => includeColumns.includes(col.accessor))}
      />
    </Modal>
  );
};

CollectedByPaymentModal.propTypes = {
  items: PropTypes.object.isRequired,
  close: PropTypes.func.isRequired,
  includeColumns: PropTypes.arrayOf(PropTypes.string),
  includeRows: PropTypes.arrayOf(PropTypes.string),
};

CollectedByPaymentModal.defaultProps = {
  includeColumns: ['type', 'pos', 'squire', 'cash', 'giftCard'],
  includeRows: ['net', 'tips', 'tax', 'fees', 'total'],
};

export default CollectedByPaymentModal;
